import { Injectable } from '@angular/core';

/**
 * One-shot latch for the Flexi status-emoji entry animation.
 *
 * The acceptance criterion is "animates only upon initial routing into the
 * Flexi tab" — not on every tab switch inside Flexi, and not again when the
 * compliance score reloads. So:
 *
 *  - FlexiComponent arms the latch in its constructor, which is the moment the
 *    router activates /Flexi. Leaving Flexi and coming back re-mounts
 *    FlexiComponent and therefore re-arms.
 *  - The first emoji that actually renders consumes the latch, and every emoji
 *    after it — in the same view or another Flexi tab — renders unanimated.
 *
 * Consuming at render time rather than in ngOnInit matters: the score chip on
 * the General page only appears once the /final round-trip lands, and a
 * business with no completed assessment never shows it at all. Latching in
 * ngOnInit would burn the animation on a chip that never appeared.
 *
 * Root-provided so the latch is shared even if FlexiModule is instantiated
 * more than once.
 */
@Injectable({ providedIn: 'root' })
export class FlexiEmojiEntryService {
  private armed = false;

  /** Called when the router enters /Flexi. */
  arm(): void {
    this.armed = !this.prefersReducedMotion();
  }

  /**
   * True at most once per Flexi entry. Call this from the component the first
   * time it needs the animation state, then cache the answer — re-reading it
   * on later change-detection passes must not re-trigger anything.
   */
  consume(): boolean {
    const wasArmed = this.armed;
    this.armed = false;
    return wasArmed;
  }

  /**
   * Users who asked their OS for less motion get the emoji, just not the
   * bounce. Guarded for non-browser / older-browser hosts.
   */
  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
